<script setup>
import { computed, reactive, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import ConversationApi from 'dashboard/api/inbox/conversation';
import MessageApi from 'dashboard/api/inbox/message';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['updated', 'deleted']);

const { t } = useI18n();
const router = useRouter();
const dealsStore = useDealsStore();
const accountId = useMapGetter('getCurrentAccountId');

const dialogRef = ref(null);
const deal = ref(null);
const selectedStageId = ref(null);
const form = reactive({ name: '', amount: '', expectedCloseOn: '' });

const conversations = ref([]);
const isLoadingConversations = ref(false);
const isSaving = ref(false);

const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
let searchTimer = null;

// "Le mie società": quali entità nostre (fino a 2) seguono la trattativa con quel contatto.
// Campo libero, compilato a mano, con autocomplete dai nomi già usati sugli altri deal.
const MAX_OWNER_COMPANIES = 2;
const ownerCompanies = ref([]);
const companyInput = ref('');
const isSavingCompanies = ref(false);

// Mappa canale -> icona/etichetta, così una conversazione mostra da dove arriva
// (WhatsApp, Telegram, Mail...). TelegramUser condivide l'icona di Telegram.
const CHANNEL_META = {
  'Channel::Whatsapp': { icon: 'i-ri-whatsapp-fill', label: 'WhatsApp' },
  'Channel::Telegram': { icon: 'i-ri-telegram-fill', label: 'Telegram' },
  'Channel::TelegramUser': { icon: 'i-ri-telegram-fill', label: 'Telegram' },
  'Channel::Email': { icon: 'i-ri-mail-fill', label: 'Email' },
  'Channel::Sms': { icon: 'i-ri-chat-1-fill', label: 'SMS' },
  'Channel::TwilioSms': { icon: 'i-ri-chat-1-fill', label: 'SMS' },
  'Channel::WebWidget': { icon: 'i-ri-global-fill', label: 'Chat' },
  'Channel::Api': { icon: 'i-ri-cloudy-fill', label: 'API' },
};

const channelMeta = channelType =>
  CHANNEL_META[channelType] || {
    icon: 'i-ri-chat-1-fill',
    label: (channelType || '').replace('Channel::', '') || 'Chat',
  };

const pipelineStages = computed(() => {
  const pipeline = dealsStore.pipelines.find(
    p => p.id === deal.value?.pipelineId
  );
  return pipeline?.stages || dealsStore.stages;
});

const stageOptions = computed(() =>
  pipelineStages.value.map(stage => ({ value: stage.id, label: stage.name }))
);

const isFormInvalid = computed(() => !form.name.trim());

const linkedIds = computed(
  () => new Set(conversations.value.map(conversation => conversation.id))
);

// Anteprima chat: aprendo il deal si vede subito l'ultima conversazione senza uscire.
const previewMessages = ref([]);
const isLoadingPreview = ref(false);
const threadRef = ref(null);

// Conversazione principale = la più recente tra quelle collegate.
const primaryConversation = computed(() => {
  if (!conversations.value.length) return null;
  return [...conversations.value].sort(
    (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
  )[0];
});

const messageFallback = message =>
  message.attachments?.length
    ? t('DEALS.DETAIL.PREVIEW.ATTACHMENT')
    : t('DEALS.DETAIL.PREVIEW.NO_CONTENT');

const loadPreview = async () => {
  const primary = primaryConversation.value;
  previewMessages.value = [];
  if (!primary) return;
  isLoadingPreview.value = true;
  try {
    const {
      data: { payload = [] },
    } = await MessageApi.getPreviousMessages({ conversationId: primary.id });
    // Solo messaggi pubblici (niente note private), ultimi ~30 in ordine cronologico.
    previewMessages.value = payload.filter(m => !m.private).slice(-30);
    await nextTick();
    if (threadRef.value) {
      threadRef.value.scrollTop = threadRef.value.scrollHeight;
    }
  } catch (error) {
    previewMessages.value = [];
  } finally {
    isLoadingPreview.value = false;
  }
};

const fetchConversations = async () => {
  if (!deal.value) return;
  isLoadingConversations.value = true;
  try {
    conversations.value = await dealsStore.listDealConversations(deal.value.id);
  } catch (error) {
    conversations.value = [];
  } finally {
    isLoadingConversations.value = false;
  }
  loadPreview();
};

const open = dealToOpen => {
  deal.value = { ...dealToOpen };
  selectedStageId.value = dealToOpen.pipelineStageId;
  form.name = dealToOpen.name || '';
  form.amount = dealToOpen.amountCents
    ? String(dealToOpen.amountCents / 100)
    : '';
  form.expectedCloseOn = dealToOpen.expectedCloseOn || '';
  ownerCompanies.value = Array.isArray(dealToOpen.ownerCompanies)
    ? [...dealToOpen.ownerCompanies]
    : [];
  companyInput.value = '';
  searchQuery.value = '';
  searchResults.value = [];
  conversations.value = [];
  dialogRef.value?.open();
  fetchConversations();
};

const canAddCompany = computed(
  () => ownerCompanies.value.length < MAX_OWNER_COMPANIES
);

// Suggerimenti dai nomi già usati sugli altri deal, esclusi quelli già selezionati e
// filtrati sul testo digitato.
const companySuggestions = computed(() => {
  const query = companyInput.value.trim().toLowerCase();
  const selected = new Set(
    ownerCompanies.value.map(name => name.toLowerCase())
  );
  return dealsStore.ownerCompanySuggestions
    .filter(name => !selected.has(name.toLowerCase()))
    .filter(name => !query || name.toLowerCase().includes(query))
    .slice(0, 6);
});

// Mostra la riga "aggiungi «testo»" solo quando il testo non è già tra i selezionati o
// i suggerimenti proposti.
const typedIsNew = computed(() => {
  const value = companyInput.value.trim().toLowerCase();
  if (!value) return false;
  const existing = new Set([
    ...ownerCompanies.value.map(name => name.toLowerCase()),
    ...companySuggestions.value.map(name => name.toLowerCase()),
  ]);
  return !existing.has(value);
});

// Salva l'elenco corrente sul deal e aggiorna la card subito (come per lo stage).
const persistOwnerCompanies = async () => {
  isSavingCompanies.value = true;
  try {
    const updated = await dealsStore.updateDeal(deal.value.id, {
      ownerCompanies: ownerCompanies.value,
    });
    deal.value = { ...deal.value, ...updated };
    ownerCompanies.value = Array.isArray(updated.ownerCompanies)
      ? [...updated.ownerCompanies]
      : ownerCompanies.value;
    emit('updated');
  } catch (error) {
    useAlert(error.message || t('DEALS.DETAIL.API.ERROR'));
  } finally {
    isSavingCompanies.value = false;
  }
};

const addCompany = name => {
  const value = (name ?? companyInput.value).trim();
  companyInput.value = '';
  if (!value || !canAddCompany.value) return;
  // Niente duplicati (case-insensitive).
  if (ownerCompanies.value.some(c => c.toLowerCase() === value.toLowerCase()))
    return;
  ownerCompanies.value = [...ownerCompanies.value, value].slice(
    0,
    MAX_OWNER_COMPANIES
  );
  persistOwnerCompanies();
};

const removeCompany = name => {
  ownerCompanies.value = ownerCompanies.value.filter(c => c !== name);
  persistOwnerCompanies();
};

const onStageChange = async value => {
  selectedStageId.value = value;
  try {
    const updated = await dealsStore.changeDealStage(deal.value.id, value);
    deal.value = { ...deal.value, ...updated };
    emit('updated');
  } catch (error) {
    useAlert(error.message || t('DEALS.DETAIL.API.STAGE_ERROR'));
  }
};

const onSave = async () => {
  if (isFormInvalid.value) return;
  isSaving.value = true;
  try {
    const updated = await dealsStore.updateDeal(deal.value.id, {
      name: form.name.trim(),
      amountCents: Math.round(Number(form.amount || 0) * 100),
      expectedCloseOn: form.expectedCloseOn || null,
    });
    deal.value = { ...deal.value, ...updated };
    emit('updated');
    useAlert(t('DEALS.DETAIL.API.SUCCESS'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.message || t('DEALS.DETAIL.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const runSearch = async () => {
  const query = searchQuery.value.trim();
  if (query.length < 2) {
    searchResults.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const {
      data: { payload = [] },
    } = await ConversationApi.search({ q: query });
    searchResults.value = payload.filter(
      conversation => !linkedIds.value.has(conversation.id)
    );
  } catch (error) {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const onSearchInput = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 300);
};

const linkConversation = async conversation => {
  try {
    await dealsStore.linkConversation(deal.value.id, conversation.id);
    searchQuery.value = '';
    searchResults.value = [];
    await fetchConversations();
    emit('updated');
  } catch (error) {
    useAlert(error.message || t('DEALS.DETAIL.API.LINK_ERROR'));
  }
};

const unlinkConversation = async conversation => {
  try {
    await dealsStore.unlinkConversation(deal.value.id, conversation.id);
    await fetchConversations();
    emit('updated');
  } catch (error) {
    useAlert(error.message || t('DEALS.DETAIL.API.UNLINK_ERROR'));
  }
};

const openConversation = conversation => {
  dialogRef.value?.close();
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversation.id },
  });
};

const onDelete = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('DEALS.DETAIL.DELETE_CONFIRM'))) return;
  try {
    await dealsStore.deleteDeal(deal.value.id);
    emit('deleted', deal.value.id);
    useAlert(t('DEALS.DETAIL.API.DELETE_SUCCESS'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.message || t('DEALS.DETAIL.API.DELETE_ERROR'));
  }
};

const searchResultName = conversation =>
  conversation.contact?.name || `#${conversation.id}`;

// Sottotitolo della conversazione collegata (canale · inbox · #id) costruito in JS
// per non lasciare testo grezzo nel template.
const conversationSubtitle = conversation => {
  const parts = [channelMeta(conversation.channelType).label];
  if (conversation.inboxName) parts.push(conversation.inboxName);
  parts.push(`#${conversation.id}`);
  return parts.join(' · ');
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div v-if="deal" class="flex flex-col gap-5">
      <!-- Anteprima chat: aprendo il deal si vede subito l'ultima conversazione,
           senza uscire dalla pagina. -->
      <div v-if="primaryConversation" class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between gap-2">
          <span
            class="flex items-center gap-1.5 text-sm font-medium min-w-0 text-n-slate-12"
          >
            <Icon
              :icon="channelMeta(primaryConversation.channelType).icon"
              class="size-4 text-n-slate-11 shrink-0"
            />
            <span class="truncate">
              {{
                primaryConversation.contactName || `#${primaryConversation.id}`
              }}
            </span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            color="slate"
            icon="i-lucide-external-link"
            :label="t('DEALS.DETAIL.PREVIEW.OPEN')"
            @click="openConversation(primaryConversation)"
          />
        </div>
        <div
          ref="threadRef"
          class="flex flex-col gap-1.5 p-3 overflow-y-auto rounded-lg max-h-72 bg-n-alpha-1"
        >
          <div v-if="isLoadingPreview" class="flex justify-center py-4">
            <Spinner :size="16" />
          </div>
          <template v-else>
            <p
              v-if="!previewMessages.length"
              class="py-2 text-xs text-center text-n-slate-10"
            >
              {{ t('DEALS.DETAIL.PREVIEW.EMPTY') }}
            </p>
            <template v-for="message in previewMessages" :key="message.id">
              <div
                v-if="message.message_type === 2"
                class="text-xs text-center text-n-slate-10"
              >
                {{ message.content }}
              </div>
              <div
                v-else
                class="flex"
                :class="
                  message.message_type === 1 ? 'justify-end' : 'justify-start'
                "
              >
                <div
                  class="max-w-[85%] px-3 py-1.5 rounded-2xl text-sm whitespace-pre-wrap break-words"
                  :class="
                    message.message_type === 1
                      ? 'bg-n-blue-9 text-white'
                      : 'bg-n-solid-2 text-n-slate-12'
                  "
                >
                  {{ message.content || messageFallback(message) }}
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>

      <!-- Titolo e dati principali -->
      <div class="flex flex-col gap-3">
        <Input
          v-model="form.name"
          :label="t('DEALS.DETAIL.FIELDS.NAME')"
          :placeholder="t('DEALS.CREATE.FIELDS.NAME')"
          class="w-full"
        />

        <!-- "Le mie società": chi (fino a 2) sta seguendo il contatto. Campo libero con
             autocomplete dai nomi già usati; si aggiunge col + o con Invio. -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('DEALS.DETAIL.FIELDS.MY_COMPANIES') }}
          </label>
          <div v-if="ownerCompanies.length" class="flex flex-wrap gap-1.5">
            <span
              v-for="name in ownerCompanies"
              :key="name"
              class="flex items-center gap-1 py-1 ltr:pl-2 ltr:pr-1 rtl:pr-2 rtl:pl-1 text-sm rounded-lg bg-n-alpha-1 text-n-slate-12"
            >
              <Icon
                icon="i-lucide-building-2"
                class="size-3.5 text-n-slate-11 shrink-0"
              />
              <span class="truncate max-w-[10rem]">{{ name }}</span>
              <Button
                variant="ghost"
                size="sm"
                color="slate"
                icon="i-lucide-x"
                @click="removeCompany(name)"
              />
            </span>
          </div>
          <template v-if="canAddCompany">
            <Input
              v-model="companyInput"
              :placeholder="t('DEALS.DETAIL.FIELDS.MY_COMPANIES_PLACEHOLDER')"
              @keydown.enter.prevent="addCompany()"
            />
            <div
              v-for="name in companySuggestions"
              :key="`suggestion-${name}`"
              class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
              @click="addCompany(name)"
            >
              <Icon
                icon="i-lucide-building-2"
                class="size-4 text-n-slate-11 shrink-0"
              />
              <span class="text-sm truncate text-n-slate-12 grow">
                {{ name }}
              </span>
            </div>
            <div
              v-if="typedIsNew"
              class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-n-alpha-1 text-n-slate-11"
              @click="addCompany()"
            >
              <Icon icon="i-lucide-plus" class="size-4 shrink-0" />
              <span class="text-sm truncate">
                {{
                  t('DEALS.DETAIL.FIELDS.MY_COMPANIES_ADD', {
                    name: companyInput.trim(),
                  })
                }}
              </span>
            </div>
          </template>
          <p v-else class="text-xs text-n-slate-10">
            {{ t('DEALS.DETAIL.FIELDS.MY_COMPANIES_MAX') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            v-model="form.amount"
            type="number"
            min="0"
            :label="t('DEALS.DETAIL.FIELDS.AMOUNT')"
          />
          <Input
            v-model="form.expectedCloseOn"
            type="date"
            :label="t('DEALS.DETAIL.FIELDS.EXPECTED_CLOSE_ON')"
          />
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('DEALS.DETAIL.FIELDS.STAGE') }}
            </label>
            <Select
              :model-value="selectedStageId"
              :options="stageOptions"
              class="!w-full"
              @update:model-value="onStageChange"
            />
          </div>
        </div>
      </div>

      <!-- Conversazioni collegate -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('DEALS.DETAIL.CONVERSATIONS.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-10">{{
            conversations.length
          }}</span>
        </div>

        <div v-if="isLoadingConversations" class="flex justify-center py-2">
          <Spinner :size="16" />
        </div>

        <p
          v-else-if="!conversations.length"
          class="text-sm text-n-slate-11 py-1"
        >
          {{ t('DEALS.DETAIL.CONVERSATIONS.EMPTY') }}
        </p>

        <template v-else>
          <div
            v-for="conversation in conversations"
            :key="conversation.id"
            class="flex items-center gap-3 px-3 py-2 rounded-lg bg-n-alpha-1"
          >
            <Icon
              :icon="channelMeta(conversation.channelType).icon"
              class="size-4 text-n-slate-11 shrink-0"
            />
            <div class="flex flex-col min-w-0 grow">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ conversation.contactName || `#${conversation.id}` }}
              </span>
              <span class="text-xs truncate text-n-slate-10">
                {{ conversationSubtitle(conversation) }}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              color="slate"
              icon="i-lucide-external-link"
              @click="openConversation(conversation)"
            />
            <Button
              variant="ghost"
              size="sm"
              color="ruby"
              icon="i-lucide-unlink"
              @click="unlinkConversation(conversation)"
            />
          </div>
        </template>

        <!-- Ricerca e collegamento nuove conversazioni -->
        <div class="flex flex-col gap-1 mt-1">
          <Input
            v-model="searchQuery"
            :placeholder="t('DEALS.DETAIL.CONVERSATIONS.SEARCH_PLACEHOLDER')"
            @input="onSearchInput"
          />
          <div v-if="isSearching" class="flex justify-center py-2">
            <Spinner :size="16" />
          </div>
          <div
            v-for="result in searchResults"
            :key="`search-${result.id}`"
            class="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
            @click="linkConversation(result)"
          >
            <Icon
              :icon="channelMeta(result.inbox?.channel_type).icon"
              class="size-4 text-n-slate-11 shrink-0"
            />
            <span class="text-sm truncate text-n-slate-12 grow">
              {{ searchResultName(result) }}
            </span>
            <span class="text-xs text-n-slate-10">{{ `#${result.id}` }}</span>
            <Icon icon="i-lucide-plus" class="size-4 text-n-slate-11" />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          :label="t('DEALS.DETAIL.DELETE')"
          @click="onDelete"
        />
        <Button
          color="blue"
          :label="t('DEALS.DETAIL.SAVE')"
          :disabled="isFormInvalid || isSaving"
          :is-loading="isSaving"
          @click="onSave"
        />
      </div>
    </template>
  </Dialog>
</template>
